import { useState, useEffect, useCallback } from "react";
import { onSnapshot, type Query } from "firebase/firestore";

interface UseFirestoreCollectionResult<T> {
  data: T[];
  /** True until the first snapshot arrives. Later updates never flip it. */
  loading: boolean;
  error: Error | null;
  /**
   * Kept for API parity with `useFirestoreCollectionOnce` so callers can
   * swap hooks without touching mutation code. Live data needs no refetch;
   * this is a stable no-op.
   */
  refetch: () => void;
}

/**
 * Live Firestore collection subscription (onSnapshot).
 *
 * ADR-015 reserves listeners for "cases where the UI must update without
 * user action while the user is looking at it". Invitation email delivery
 * is one: the organizer presses Send, the callable queues the mail, and the
 * sent / failed flip lands seconds later from a Cloud Function trigger.
 * Polling with refetches re-rendered the whole table on every poll; a
 * listener updates the one row that changed.
 */
export function useFirestoreCollection<T>(
  query: Query | null,
): UseFirestoreCollectionResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(() => undefined, []);

  useEffect(() => {
    if (!query) {
      setData([]);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      query,
      (snapshot) => {
        setData(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as T),
        );
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [query]);

  return { data, loading, error, refetch };
}
